import React from 'react';
import { useFamilyParticipants } from '../hooks/useFamilyParticipants';

function EventFilters({ 
  filters, 
  onFilterChange,
  isMobile 
}) {
  // Get participants from family configuration (same as in event modals)
  const { participants: familyParticipants } = useFamilyParticipants();
  const participants = familyParticipants || [];

  // Event categories
  const categories = [
    { id: 'school', name: 'School' },
    { id: 'work', name: 'Work' },
    { id: 'sport', name: 'Sport' },
    { id: 'life', name: 'Life' },
    { id: 'other', name: 'Other' }
  ];

  // Toggle participant filter
  const toggleParticipantFilter = (participantId) => {
    const updatedParticipants = filters.participants.includes(participantId)
      ? filters.participants.filter(id => id !== participantId)
      : [...filters.participants, participantId];
    
    onFilterChange({
      ...filters,
      participants: updatedParticipants
    });
  };

  // Toggle category filter
  const toggleCategoryFilter = (categoryId) => {
    const updatedCategories = filters.categories.includes(categoryId)
      ? filters.categories.filter(id => id !== categoryId)
      : [...filters.categories, categoryId];
    
    onFilterChange({
      ...filters,
      categories: updatedCategories
    });
  };

  // Clear all filters
  const clearFilters = () => {
    onFilterChange({
      participants: [],
      categories: []
    });
  };

  return (
    <div className="event-filters" style={{
      fontSize: '0.9rem',
      backgroundColor: 'transparent',
      padding: '1rem 1.75rem', // Added more left padding to align with tabs
      borderRadius: '0',
      animation: 'fadeIn 0.3s ease-in-out',
    }}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .filter-button {
            transition: all 0.2s ease-in-out !important;
          }

          .filter-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .filter-button.active {
            transform: scale(1.05);
          }

          .clear-filters-button {
            transition: all 0.2s ease-in-out;
          }

          .clear-filters-button:hover {
            background-color: rgba(28, 131, 201, 0.1) !important;
            color: #1c83c9 !important;
          }
        `}
      </style>
      <div className="filter-header" style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '5px',
        borderBottom: 'none',
        paddingBottom: '0'
      }}>
        {(filters.participants.length > 0 || filters.categories.length > 0) && (
          <button
            className="clear-filters-button"
            onClick={clearFilters}
            style={{
              fontSize: '0.8rem',
              borderRadius: '6px',
              padding: '5px 10px',
              border: 'none',
              backgroundColor: 'rgba(0,0,0,0.05)',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>
      
      <div className="filter-section" style={{ 
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: '10px',
        gap: '8px'
      }}>
        <span style={{ 
          fontSize: '1.05rem', 
          fontWeight: '400',
          margin: '0',
          marginTop: '5px', // Add top margin to align with first row of buttons
          whiteSpace: 'nowrap',
          color: '#000'
        }}>People:</span>
        <div className="filter-buttons" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px',
          paddingLeft: '0', // Ensure no padding pushes buttons off alignment
          alignItems: 'flex-start'
        }}>
          {participants.map(person => (
            <button
              key={person.id}
              className={`filter-button participant-filter 
                ${filters.participants.includes(person.id) ? 'active' : ''}`}
              onClick={() => toggleParticipantFilter(person.id)}
              style={{ fontSize: '0.85rem', padding: '5px 8px' }}
            >
              {person.name}
              {person.custom && (
                <span style={{marginLeft: '4px', fontSize: '10px', opacity: 0.7}}>*</span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      <div className="filter-section" style={{ 
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: '10px',
        gap: '8px'
      }}>
        <span style={{ 
          fontSize: '1.05rem',
          fontWeight: '400',
          margin: '0',
          marginTop: '5px', // Add top margin to align with first row of buttons
          whiteSpace: 'nowrap',
          color: '#000'
        }}>Types:</span>
        <div className="filter-buttons" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px',
          paddingLeft: '0', // Ensure no padding pushes buttons off alignment
          alignItems: 'flex-start'
        }}>
          {categories.map(category => (
            <button
              key={category.id}
              className={`filter-button category-filter ${category.id} 
                ${filters.categories.includes(category.id) ? 'active' : ''}`}
              onClick={() => toggleCategoryFilter(category.id)}
              style={{ fontSize: '0.85rem', padding: '5px 8px' }}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
      
      {(filters.participants.length > 0 || filters.categories.length > 0) && (
        <div className="active-filters" style={{
          marginTop: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          animation: 'slideIn 0.3s ease-in-out'
        }}>
          <style>
            {`
              @keyframes slideIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }

              .filter-tag {
                animation: popIn 0.3s ease-in-out;
                transition: all 0.2s ease-in-out;
              }

              .filter-tag:hover {
                transform: scale(1.05);
              }

              @keyframes popIn {
                0% { opacity: 0; transform: scale(0.8); }
                70% { transform: scale(1.1); }
                100% { opacity: 1; transform: scale(1); }
              }
            `}
          </style>
          <span style={{
            fontSize: '0.9rem',
            fontWeight: '400',
            margin: '0',
            whiteSpace: 'nowrap',
            color: '#666'
          }}>Active Filters:</span>
          <div className="active-filter-tags" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '5px'
          }}>
            {filters.participants.map(id => {
              const person = participants.find(p => p.id === id);
              return person ? (
                <div key={id} className="filter-tag participant-tag" style={{ 
                  fontSize: '0.8rem',
                  padding: '3px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  {person.name}
                  <button 
                    className="remove-filter" 
                    onClick={() => toggleParticipantFilter(id)}
                    style={{ fontSize: '1rem', padding: '0 2px' }}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
            
            {filters.categories.map(id => {
              const category = categories.find(c => c.id === id);
              return category ? (
                <div key={id} className={`filter-tag category-tag ${id}`} style={{ 
                  fontSize: '0.8rem',
                  padding: '3px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  {category.name}
                  <button 
                    className="remove-filter" 
                    onClick={() => toggleCategoryFilter(id)}
                    style={{ fontSize: '1rem', padding: '0 2px' }}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventFilters;